/* eslint-disable sonarjs/cognitive-complexity, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import { HTMLAttributes, ReactNode, useEffect, useRef, useId } from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'
import { Button, Heading, Icon } from '@alto-avios/alto-ui'
import styles from './Modal.module.scss'

export type ModalProps = HTMLAttributes<HTMLDialogElement> & {
  id?: string
  isOpen: boolean
  onClose: () => void
  title?: string
  preventScroll?: boolean
  isCentered?: boolean
  titleTextAlign?: 'left' | 'center' | 'right'
  size?: 'sm' | 'md' | 'xl' | 'full'
  headingTag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  variant?: 'solid' | 'transparent'
  zIndex?: number
  maxWidth?: string
}

function Modal(props: ModalProps): ReactNode {
  const {
    id,
    isOpen,
    onClose,
    children,
    preventScroll = true,
    title,
    isCentered = true,
    titleTextAlign = 'center',
    className,
    size = 'xl',
    headingTag = 'h2',
    variant = 'solid',
    role = 'dialog',
    zIndex,
    maxWidth,
    style,
    ...other
  } = props

  const modalRoot = typeof window !== 'undefined' ? document.getElementById('modal') : null
  const modalRef = useRef<HTMLDialogElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)
  const wasOpenRef = useRef(isOpen)
  const titleId = useId()

  // Prevent background scroll when modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow ?? ''
    if (preventScroll && isOpen) {
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.body.style.overflow = originalOverflow
    }
  }, [preventScroll, isOpen])

  useEffect(() => {
    const mainContent = document.getElementById('__next')
    if (mainContent) {
      if (isOpen) {
        mainContent.setAttribute('aria-hidden', 'true')
      } else {
        mainContent.removeAttribute('aria-hidden')
      }
    }
  }, [isOpen])

  // Focus modal on open and restore previous focus on close.
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement
      modalRef.current?.focus()
    }

    if (wasOpenRef.current && !isOpen) {
      const previousFocus = previousFocusRef.current
      if (previousFocus && document.contains(previousFocus)) {
        window.requestAnimationFrame(() => {
          previousFocus.focus()
        })
      }
    }

    wasOpenRef.current = isOpen
  }, [isOpen])

  // Restore focus to the previously focused element when modal unmounts
  useEffect(
    () => () => {
      if (previousFocusRef.current && document.contains(previousFocusRef.current)) {
        previousFocusRef.current.focus()
      }
    },
    []
  )

  // Close modal on pressing the Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
      if (e.key === 'Tab' && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
        if (focusable.length === 0) {
          return
        }

        const first = focusable[0]
        const last = focusable[focusable.length - 1]

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown)
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  if (!isOpen || !modalRoot) {
    return null
  }

  return createPortal(
    <>
      <div className={styles.overlay} onClick={onClose} style={{ ...(zIndex ? { zIndex } : {}) }} />
      <dialog
        ref={modalRef}
        onClose={onClose}
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        className={classNames(
          styles.modal,
          className,
          size ? styles[size] : undefined,
          isCentered ? styles.centered : undefined,
          styles[variant]
        )}
        style={{ ...style, ...(maxWidth ? { maxWidth } : {}), ...(zIndex ? { zIndex } : {}) }}
        data-testid="modal"
        id={id}
        open={isOpen}
        role={role}
        tabIndex={-1}
        {...other}
      >
        <div className={styles.container}>
          {variant === 'solid' ? (
            <div className={classNames(styles.header, title ? styles[titleTextAlign] : undefined)}>
              {title ? (
                <Heading as={headingTag} foregroundColor="accentSecondary" id={titleId}>
                  {title}
                </Heading>
              ) : null}
              <Button
                aria-label="Close modal"
                data-testid="modal_close_button"
                onPress={onClose}
                styleVariant="white"
              >
                <Icon iconName="xmark" iconSize="1.5x" />
              </Button>
            </div>
          ) : null}
          <div className={styles.content} data-testid="modal_content">
            {children}
          </div>
        </div>
      </dialog>
    </>,
    modalRoot
  )
}

export default Modal
